import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from "react-redux";

const Header = (props) => {
  const { withcookieData, headerContainerClass, headerPaddingClass, headerPositionClass } = props;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  
  // 로그인 상태 확인
  const isUserLoggedIn = useSelector(state => state.withcookie?.isUserLoggedIn);

  // 메뉴 데이터 가져오기
  const menuData = withcookieData?.skin?.menuJsonNonUser || {};
  const mainMenus = Array.isArray(menuData.main_menus) ? menuData.main_menus : [];
  const globalMenus = Array.isArray(menuData.global_menus) ? menuData.global_menus : [];
  
  // 현재 활성 메뉴 확인
  const isActive = (path) => {
    return location.pathname === path || 
           (path !== '/' && location.pathname.startsWith(path));
  };

  // 로그인 상태에 따른 메뉴 필터링
  const filteredMainMenus = mainMenus.filter(menu => {
    // 로그인한 사용자에게만 표시
    if (menu.is_logged && !isUserLoggedIn) return false;
    
    // 로그인하지 않은 사용자에게만 표시
    if (menu.is_not_logged && isUserLoggedIn) return false;
    
    return true;
  });

  // 메뉴 트리 구성
  const buildMenuTree = (menus, parentId = null) => {
    return menus
      .filter(menu => menu.parentId === parentId)
      .map(menu => ({
        ...menu,
        children: buildMenuTree(menus, menu.id)
      }));
  };

  const menuTree = buildMenuTree(filteredMainMenus);

  // 사이드바 토글 함수
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // 클릭 이벤트 감지하여 사이드바 닫기
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (sidebarOpen && !e.target.closest('.panelbox') && !e.target.closest('.headerButton')) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [sidebarOpen]);

  // 라우트 변경 시 사이드바 닫기
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* App Header */}
      <div className={`appHeader bg-primary text-light ${headerPositionClass || ''} ${headerPaddingClass || ''}`}>
        <div className="left">
          <a href="#" className="headerButton" onClick={(e) => { e.preventDefault(); toggleSidebar(); }}>
            <ion-icon name="menu-outline"></ion-icon>
          </a>
        </div>
        <div className="pageTitle">
          <Link to="/">
            <img src={withcookieData?.skin?.logo_url || "/assets_finapp/img/logo.png"} alt="logo" className="logo" />
          </Link>
        </div>
        <div className="right">
          <Link to="/notifications" className="headerButton">
            <ion-icon className="icon" name="notifications-outline"></ion-icon>
            <span className="badge badge-danger">4</span>
          </Link>
          <Link to="/settings" className="headerButton">
            {isUserLoggedIn ? (
              <img src={withcookieData?.user?.profile_image || "/assets_finapp/img/sample/avatar/avatar1.jpg"} 
                   alt="user" className="imaged w32" />
            ) : (
              <ion-icon name="person-circle-outline" style={{ fontSize: '32px' }}></ion-icon>
            )}
          </Link>
        </div>
      </div>
      {/* * App Header */}
      
      {/* App Sidebar */}
      <div className={`modal fade panelbox panelbox-left ${sidebarOpen ? 'show' : ''}`} 
           id="sidebarPanel" tabIndex="-1" role="dialog" 
           style={{ display: sidebarOpen ? 'block' : 'none' }}>
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-body p-0">
              {/* Profile Box */}
              <div className="profileBox pt-2 pb-2">
                <div className="image-wrapper">
                  {isUserLoggedIn ? (
                    <img src={withcookieData?.user?.profile_image || "/assets_finapp/img/sample/avatar/avatar1.jpg"} 
                         alt="user" className="imaged w36" />
                  ) : (
                    <ion-icon name="person-circle-outline" style={{ fontSize: '36px' }}></ion-icon>
                  )}
                </div>
                <div className="in">
                  {isUserLoggedIn ? (
                    <>
                      <strong>{withcookieData?.user?.name || '사용자'}</strong>
                      <div className="text-muted">{withcookieData?.user?.email || ''}</div>
                    </>
                  ) : (
                    <Link to="/login">로그인</Link>
                  )}
                </div>
                <a href="#" className="btn btn-link btn-icon sidebar-close" 
                   onClick={(e) => { e.preventDefault(); setSidebarOpen(false); }}>
                  <ion-icon name="close-outline"></ion-icon>
                </a>
              </div>
              {/* * Profile Box */}

              {/* Menu */}
              <div className="listview-title mt-1">메뉴</div>
              <ul className="listview flush transparent no-line image-listview">
                {menuTree.map((menu, index) => (
                  <li key={menu.id || index}>
                    <Link to={menu.path} className={`item ${isActive(menu.path) ? 'active' : ''}`}>
                      <div className="icon-box bg-primary">
                        <ion-icon name={menu.icon || "document-text-outline"}></ion-icon>
                      </div>
                      <div className="in">
                        {menu.name}
                        {menu.children && menu.children.length > 0 && (
                          <span className="badge badge-primary">{menu.children.length}</span>
                        )}
                      </div>
                    </Link>
                    {menu.children && menu.children.length > 0 && (
                      <ul className="submenu">
                        {menu.children.map((subMenu, subIndex) => (
                          <li key={subMenu.id || `${index}-${subIndex}`}>
                            <Link to={subMenu.path} className={isActive(subMenu.path) ? 'active' : ''}>
                              {subMenu.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              {/* * Menu */}

              {/* Global Menu / Footer Menu */}
              {globalMenus && globalMenus.length > 0 && (
                <>
                  <div className="listview-title mt-1">기타</div>
                  <ul className="listview flush transparent no-line image-listview">
                    {globalMenus.filter(menu => {
                      if (menu.is_logged && !isUserLoggedIn) return false;
                      if (menu.is_not_logged && isUserLoggedIn) return false;
                      return true;
                    }).map((menu, index) => (
                      <li key={menu.id || `global-${index}`}>
                        <Link to={menu.path} className={`item ${isActive(menu.path) ? 'active' : ''}`}>
                          <div className="icon-box bg-primary">
                            <ion-icon name={menu.icon || "settings-outline"}></ion-icon>
                          </div>
                          <div className="in">
                            {menu.name}
                          </div>
                        </Link>
                      </li>
                    ))}
                    {isUserLoggedIn && (
                      <li>
                        <Link to="/logout" className="item">
                          <div className="icon-box bg-primary">
                            <ion-icon name="log-out-outline"></ion-icon>
                          </div>
                          <div className="in">
                            로그아웃
                          </div>
                        </Link>
                      </li>
                    )}
                  </ul>
                </>
              )}
              {/* * Global Menu */}
            </div>
          </div>
        </div>
      </div>
      {/* * App Sidebar */}

      {/* 배경 오버레이 */}
      {sidebarOpen && (
        <div className="modal-backdrop fade show" 
             onClick={() => setSidebarOpen(false)}></div>
      )}
    </>
  );
};

Header.propTypes = {
  withcookieData: PropTypes.object,
  headerContainerClass: PropTypes.string,
  headerPaddingClass: PropTypes.string,
  headerPositionClass: PropTypes.string
};

export default Header; 